import { useParams, Navigate, Link } from "react-router-dom";
import Seo from "@/components/Seo";

const CONTENT = {
    privacy: {
        title: "Privacy Policy",
        intro: "ClaimHub UK Ltd is a data controller under the UK GDPR. We treat your personal data with the utmost care.",
        sections: [
            { h: "What we collect", p: "Name, contact details, postcode, and the details of your claim (e.g. finance agreement, tenancy, incident). We never collect data we don&apos;t need." },
            { h: "How we use it", p: "Solely to assess your claim, match you with a specialist firm and keep you updated. We never sell your data to third parties." },
            { h: "How we protect it", p: "Bank-grade encryption in transit and at rest, UK-based data centres, ICO-registered, ISO 27001 aligned." },
            { h: "Your rights", p: "Access, rectification, erasure, portability and objection. Email privacy@claimhubuk.co.uk to exercise any right within 30 days." },
            { h: "Cookies", p: "We use essential cookies for the site to function and (with consent) analytics cookies to improve it. See our Cookie Policy for details." },
        ],
    },
    terms: {
        title: "Terms of Business",
        intro: "These terms govern your relationship with ClaimHub UK Ltd. Please read carefully — you receive a signed copy before any claim begins.",
        sections: [
            { h: "Our service", p: "We assess your claim and where appropriate refer it to a panel of SRA-regulated solicitors who pursue the matter on your behalf." },
            { h: "Fees", p: "100% No Win, No Fee. Our success fee is capped within FCA limits (typically 18 – 30% +VAT of the compensation recovered) and disclosed in writing in advance. If your claim is unsuccessful, you pay nothing." },
            { h: "Cancellation", p: "You may cancel within 14 days of signing with no charge. After 14 days, fair time-and-effort costs may apply — set out in your client agreement." },
            { h: "Your alternatives", p: "You can complain directly to the firm, regulator or Financial Ombudsman Service for free without using a claims management company." },
        ],
    },
    complaints: {
        title: "Complaints Procedure",
        intro: "We&apos;re committed to providing an exceptional service. If something falls short, we want to know — and we&apos;ll put it right.",
        sections: [
            { h: "Step 1 – Tell us", p: "Email complaints@claimhubuk.co.uk or write to: Complaints Team, ClaimHub UK Ltd, 71-75 Shelton Street, Covent Garden, London WC2H 9JQ. Acknowledged within 3 working days." },
            { h: "Step 2 – Investigation", p: "We investigate and provide a substantive response within 8 weeks." },
            { h: "Step 3 – Escalation", p: "If you remain dissatisfied, you can refer the matter to the Financial Ombudsman Service (financial-ombudsman.org.uk) free of charge, within 6 months of our final response." },
        ],
    },
    cookies: {
        title: "Cookie Policy",
        intro: "We use the minimum cookies needed to make the site work, plus optional analytics cookies to improve it.",
        sections: [
            { h: "Essential cookies", p: "Allow you to navigate the site and use core features (e.g. submitting a claim, logging into the admin)." },
            { h: "Analytics cookies", p: "PostHog product analytics — only loaded if you accept. Helps us understand which pages convert and where we can improve UX." },
            { h: "Managing cookies", p: "You can disable cookies in your browser at any time. Doing so may break parts of the site." },
        ],
    },
    gdpr: {
        title: "GDPR & Your Data",
        intro: "Your data is yours. UK GDPR gives you strong rights — we make exercising them easy.",
        sections: [
            { h: "Access", p: "Request a copy of all data we hold about you. We respond within 30 days, free of charge." },
            { h: "Erasure", p: "Ask us to delete your data. Where there&apos;s no overriding legal reason to keep it, we will." },
            { h: "Portability", p: "Get a machine-readable copy of your data to take elsewhere." },
            { h: "Contact our DPO", p: "Email dpo@claimhubuk.co.uk. Registered with the ICO under registration ZA000000 (example)." },
        ],
    },
};

export default function LegalPage() {
    const { slug } = useParams();
    const c = CONTENT[slug];
    if (!c) return <Navigate to="/" replace />;

    return (
        <>
            <Seo title={c.title} description={c.intro} path={`/legal/${slug}`} />
            <section className="bg-navy-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-mesh-emerald" />
                <div className="absolute inset-0 grid-bg-dark opacity-25" />
                <div className="container-x relative py-16 lg:py-20">
                    <span className="text-eyebrow-light">Legal</span>
                    <h1 className="mt-3 text-4xl sm:text-5xl font-display font-bold tracking-tight">{c.title}</h1>
                    <p className="mt-5 text-base text-slate-300 max-w-2xl">{c.intro}</p>
                </div>
            </section>

            <section className="section-pad bg-white">
                <div className="container-x grid lg:grid-cols-12 gap-10">
                    <aside className="lg:col-span-3">
                        <div className="lg:sticky lg:top-28">
                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 mb-3">All legal pages</div>
                            <ul className="space-y-1.5">
                                {Object.entries(CONTENT).map(([s, v]) => (
                                    <li key={s}>
                                        <Link to={`/legal/${s}`} className={`block py-2 text-sm rounded-lg px-3 ${s === slug ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
                                            {v.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>
                    <article className="lg:col-span-9 space-y-10">
                        {c.sections.map((s) => (
                            <div key={s.h}>
                                <h2 className="text-xl sm:text-2xl font-display font-semibold text-navy-900">{s.h}</h2>
                                <p className="mt-3 text-base text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: s.p }} />
                            </div>
                        ))}
                        <div className="text-xs text-slate-400 pt-6 border-t border-slate-100">Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
                    </article>
                </div>
            </section>
        </>
    );
}
